import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building2, Users, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOrgDetail = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrgData();
  }, [orgId]);

  const getToken = () => localStorage.getItem('access_token');

  const fetchOrgData = async () => {
    try {
      const response = await axios.get(`${API}/admin/orgs/${orgId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!data) return <div className="p-6">Organization not found</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/database-admin')}>
          <ArrowLeft size={16} className="mr-2" />
          Back to Search
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold text-slate-900">Organization: {data.organization.name}</h1>
        <div className="flex gap-2 mt-2">
          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
            {data.organization.status || 'active'}
          </span>
          <span className="text-xs text-slate-500">
            Created: {data.organization.created_at?.split('T')[0]}
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Users */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users size={20} />
              Users ({data.users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.users.map((user) => (
                <div key={user.id} className="p-3 border rounded-lg">
                  <p className="font-medium">{user.name}</p>
                  <p className="text-sm text-slate-600">{user.email}</p>
                  <span className="text-xs bg-slate-100 px-2 py-1 rounded mt-1 inline-block">
                    {user.role}
                  </span>
                </div>
              ))}
              {data.users.length === 0 && (
                <p className="text-slate-500 text-center py-4">No users</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Properties */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 size={20} />
              Properties ({data.properties.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.properties.map((prop) => (
                <div key={prop.id} className="p-3 border rounded-lg">
                  <p className="font-medium">{prop.name}</p>
                  <p className="text-sm text-slate-600">{[prop.address, prop.city, prop.state, prop.zip_code].filter(Boolean).join(', ') || 'N/A'}</p>
                  <div className="flex gap-3 mt-2 text-xs text-slate-500">
                    <span>{prop.room_types_count} room types</span>
                    <span>{prop.rooms_count} rooms</span>
                    <span>{prop.reservations_count} reservations</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 w-full"
                    onClick={() => navigate(`/admin-property/${orgId}/${prop.id}`)}
                  >
                    Open Property
                  </Button>
                </div>
              ))}
              {data.properties.length === 0 && (
                <p className="text-slate-500 text-center py-4">No properties</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminOrgDetail;